import type { Item } from '@/lib/api/types/items';

/**
 * Extracts a single string value from a built-in or custom field for color mapping.
 * For multi-value fields (arrays), returns only the first value.
 */
function extractSingleValue(value: unknown): string | undefined {
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  if (typeof value === 'boolean') return String(value);
  if (value && typeof value === 'object') {
    const keys = Object.keys(value).filter((k) => k !== 'id');
    const [key] = keys;
    if (keys.length === 1 && key !== undefined) {
      const inner = (value as Record<string, unknown>)[key];
      if (typeof inner === 'number' || typeof inner === 'string' || typeof inner === 'boolean') {
        return String(inner);
      }
    }
  }
  return undefined;
}

/**
 * Extracts ALL string values from a built-in or custom field.
 * For multi-value fields (arrays), returns every value.
 */
export function getAllItemFieldValues(item: Item, fieldId: string): string[] {
  if (fieldId === 'status') return item.itemStatusId ? [item.itemStatusId.toString()] : [];
  if (fieldId === 'team') return item.teamId ? [item.teamId.toString()] : [];
  if (fieldId === 'parentItem') return item.parentId ? [item.parentId.toString()] : [];
  if (fieldId === 'createdBy') return item.createdBy != null ? [item.createdBy] : [];
  if (fieldId === 'estimationId') return item.estimationId ? [item.estimationId.toString()] : [];

  if (!item.customFields || !(fieldId in item.customFields)) return [];
  const val = item.customFields[fieldId];
  if (val == null) return [];

  if (Array.isArray(val)) {
    return val.map(extractSingleValue).filter((v): v is string => v !== undefined);
  }

  const single = extractSingleValue(val);
  return single !== undefined ? [single] : [];
}

export function getItemFieldValue(item: Item, fieldId: string): string | undefined {
  const all = getAllItemFieldValues(item, fieldId);
  return all.length > 0 ? all[0] : undefined;
}
